import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SocketContext } from '../socket';
import { voiceApi, errorMessage } from './voiceApi';
import { isUnassigned } from './callFormat';

const MAX_CARDS = 150;
const ms = (d) => new Date(d || 0).getTime();

/** One sorted, de-duplicated card list. `incremental` uses a since-cursor; otherwise every refresh is full. */
const useCardList = ({ limit, unassigned, incremental }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const newestRef = useRef(null);

    const merge = useCallback((incoming, { replace = false } = {}) => {
        setCards((prev) => {
            const byId = new Map(replace ? [] : prev.map((c) => [c.id, c]));
            for (const raw of incoming || []) {
                const card = { ...raw, updatedAtMs: ms(raw.updatedAt) };
                const existing = byId.get(card.id);
                if (!existing || existing.updatedAtMs <= card.updatedAtMs) byId.set(card.id, card);
                if (!newestRef.current || card.updatedAtMs > ms(newestRef.current)) newestRef.current = card.updatedAt;
            }
            return [...byId.values()]
                .filter((c) => !unassigned || isUnassigned(c)) // an attached call leaves the unassigned list
                .sort((a, b) => b.updatedAtMs - a.updatedAtMs)
                .slice(0, MAX_CARDS);
        });
    }, [unassigned]);

    const refresh = useCallback(async (full = false) => {
        setLoading(true);
        try {
            const useSince = incremental && !full && newestRef.current;
            const params = { limit };
            if (unassigned) params.unassigned = 'true';
            if (useSince) params.since = newestRef.current;
            const { activity } = await voiceApi.activity(params);
            // A since-page that came back full may have dropped older rows updated in the same
            // window (the nightly sweep touches many rows at once): fall back to a full load.
            if (useSince && activity.length >= limit) {
                const all = await voiceApi.activity({ limit, ...(unassigned ? { unassigned: 'true' } : {}) });
                merge(all.activity, { replace: !incremental });
            } else {
                merge(activity, { replace: !incremental && !useSince });
            }
            setError(null);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [limit, unassigned, incremental, merge]);

    return { cards, loading, error, refresh, merge };
};

/**
 * The activity feeds (plan §5.5), owned once by VoiceProvider so every panel sees the same cards:
 *   recent     — the last 100 calls, all admins; incremental since-polls
 *   unassigned — calls with no student (the attach-later tab). Loaded only while `unassignedWanted`
 *                (the tab is open) and always in full, because a call attached elsewhere simply
 *                stops being returned and an incremental merge could never drop it.
 * One `voice.activity` socket handler feeds both. Pages connect/disconnect the shared socket
 * themselves (this never does); while it is connected the room delivers every change and the
 * poll is skipped; otherwise a light poll fills the gap. Reconnects refresh via the socket.io
 * Manager's event, which a page's blanket `socket.off('connect')` cannot remove.
 */
export function useVoiceFeeds({ enabled = true, pollMs = 60000, unassignedWanted = false } = {}) {
    const socket = useContext(SocketContext);
    const recent = useCardList({ limit: 100, unassigned: false, incremental: true });
    const unassigned = useCardList({ limit: 100, unassigned: true, incremental: false });
    const { merge: mergeRecent, refresh: refreshRecent } = recent;
    const { merge: mergeUnassigned, refresh: refreshUnassigned } = unassigned;

    /** Push one fresh card (socket event or API response) into both lists. */
    const mergeCard = useCallback((card) => { if (card) { mergeRecent([card]); mergeUnassigned([card]); } }, [mergeRecent, mergeUnassigned]);
    const refreshAll = useCallback((full = false) => Promise.all([refreshRecent(full), unassignedWanted ? refreshUnassigned(true) : null]), [refreshRecent, refreshUnassigned, unassignedWanted]);

    useEffect(() => {
        if (!enabled) return undefined;
        refreshRecent(true);
        const interval = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            if (socket && socket.connected) return; // live via the admins room
            refreshRecent();
        }, pollMs);
        return () => clearInterval(interval);
    }, [enabled, pollMs, refreshRecent, socket]);

    useEffect(() => {
        if (!enabled || !unassignedWanted) return undefined;
        refreshUnassigned(true);
        const interval = setInterval(() => { if (!socket || !socket.connected) refreshUnassigned(true); }, pollMs * 2);
        return () => clearInterval(interval);
    }, [enabled, unassignedWanted, pollMs, refreshUnassigned, socket]);

    useEffect(() => {
        if (!enabled || !socket) return undefined;
        const onCard = (card) => mergeCard(card);
        const onReconnect = () => refreshAll();
        socket.on('voice.activity', onCard);
        socket.on('connect', onReconnect);
        const manager = socket.io;
        if (manager && typeof manager.on === 'function') manager.on('reconnect', onReconnect);
        return () => {
            socket.off('voice.activity', onCard);
            socket.off('connect', onReconnect);
            if (manager && typeof manager.off === 'function') manager.off('reconnect', onReconnect);
        };
    }, [enabled, socket, mergeCard, refreshAll]);

    return { recent, unassigned, mergeCard, refreshAll };
}

export default useVoiceFeeds;
